import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, AlertCircle, Clock, Video } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoOrderSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session ID');
      setLoading(false);
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API_URL}/api/video-orders/status/${sessionId}`);
        if (res.data.status === 'paid' || res.data.status === 'in_progress' || res.data.status === 'delivered') {
          setOrder(res.data);
          setLoading(false);
          return;
        }
        if (attempts < 10) {
          setTimeout(poll, 1500);
        } else {
          setError('Payment is still processing. Refresh in a moment.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not verify order');
        setLoading(false);
      }
    };
    poll();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Confirming your order…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl uppercase mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/events"><Button>Back to Events</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Check className="w-12 h-12 text-white" strokeWidth={3} />
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight">Order Confirmed</h1>
        <p className="text-muted-foreground mt-3 text-lg">
          {order.num_videos} custom video edit{order.num_videos !== 1 ? 's' : ''} from {order.event_name}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          ${order.total.toFixed(2)} CAD · {order.customer_email}
        </p>

        <div className="bg-card border-2 border-primary/30 rounded-2xl p-6 mt-8 text-left">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display font-bold text-xl uppercase">What happens next</h2>
              <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                <li>Our team will locate your footage from the event.</li>
                <li>We'll edit your requested videos.</li>
                <li>You'll receive a download link by email when ready.</li>
                <li>If we can't find your footage, we'll refund your order.</li>
              </ol>
              <p className="mt-4 text-sm">Most orders are delivered within 5–7 business days.</p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          <Link to="/gallery"><Button variant="outline">Back to Gallery</Button></Link>
          <Link to="/events"><Button>Browse Events</Button></Link>
        </div>
      </div>
    </div>
  );
};

export default VideoOrderSuccessPage;
